import { yupResolver } from '@hookform/resolvers/yup';
import * as Dialog from '@radix-ui/react-dialog';
import { AxiosError, AxiosInstance } from 'axios';
import { t } from 'i18next';
import React, { useEffect } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { errorMessageAdapter } from '../../../adapters';
import { MainCustomButton } from '../../../components/Buttons';
import { Input } from '../../../components/atoms';
import { CustomInput, CustomInputPhone } from '../../../components/inputs';
import { DirectorsRegisteringService } from '../../../services/DirectorsService';
import { DocumentService } from '../../../services/DocumentService';
import { useAuth } from '../../../store/auth';
import { errorToast, successToast } from '../../../utils/toasts';
import { FormDataTypeRegisterDirectors } from '../Registering/DashboardRegisteringDirectors';
import { countries } from '../../../constants/objects';
import { transformDate } from '../../../utils/date';
import { addMonths } from 'date-fns';
import { useApi } from '../../../services/hooks/apiHook';

interface IDirector {
  director: any;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onChange: () => void;
}

const today = new Date();
const sixMonthsFromNow = addMonths(today, 6);

function validateFileSize(value: any): boolean {
  if (!value.name) return true;

  return value.size <= 3000000;
}

function validateFileType(value: any): boolean {
  if (!value.name) return true;

  return (
    value.type === 'application/pdf' ||
    value.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  );
}

const EditDirectorModal = ({ director, open, onOpenChange, onChange }: IDirector) => {
  const { i18n } = useTranslation();
  const { token } = useAuth();
  const api: AxiosInstance = useApi();

  const [fileNamePassport, setFileNamePassport] = React.useState<string | null>();
  const [fileNameDocument1, setFileNameDocument1] = React.useState<string | null>();
  // const [fileNameDocument2, setFileNameDocument2] = React.useState<string | null>();
  const [fileNameProveAddress, setFileNameProveAddress] = React.useState<string | null>();
  const [loading, setLoading] = React.useState<boolean>(false);

  const validationSchema = Yup.object().shape({
    id: Yup.string(),
    name: Yup.string(),
    gender: Yup.string(),
    dateBirth: Yup.string(),
    phone: Yup.string(),
    email: Yup.string().email(t('OnboardingCreateAccountErrorEmailMustBe') || ''),
    nationality: Yup.string(),
    passportNo: Yup.string(),
    expiryDate: Yup.date()
      .min(sixMonthsFromNow, t('validations.valExpiryDate') || '')
      .required(t('Required') || '')
      .transform((_curr, orig) => transformDate(orig)),
    passportIssuingCountry: Yup.string(),
    issueDate: Yup.string(),
    address1: Yup.string(),
    address2: Yup.string(),
    postalCode: Yup.string(),
    city: Yup.string(),
    state: Yup.string(),
    neighborhood: Yup.string(),
    country: Yup.string(),
    passport: Yup.mixed()
      .test('fileSize', t('OnboardingCreateAccountErrorFileSize') || '', validateFileSize)
      .test('fileType', t('OnboardingCreateAccountErrorFileType') || '', validateFileType),
    document1: Yup.mixed()
      .test('fileSize', t('OnboardingCreateAccountErrorFileSize') || '', validateFileSize)
      .test('fileType', t('OnboardingCreateAccountErrorFileType') || '', validateFileType),
    // document2: Yup.mixed()
    //   .test('fileSize', t('OnboardingCreateAccountErrorFileSize') || '', validateFileSize)
    //   .test('fileType', t('OnboardingCreateAccountErrorFileType') || '', validateFileType),
    proveAddress: Yup.mixed()
      .test('fileSize', t('OnboardingCreateAccountErrorFileSize') || '', validateFileSize)
      .test('fileType', t('OnboardingCreateAccountErrorFileType') || '', validateFileType)
  });

  const {
    control,
    register,
    formState: { errors },
    handleSubmit,
    setValue,
    getValues,
    reset
  } = useForm<FormDataTypeRegisterDirectors>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      gender: ''
    }
  });

  useEffect(() => {
    if (!open) return;
    setFileNamePassport('');
    setFileNameDocument1('');
    setFileNameProveAddress('');
    setValue('name', director?.name);
    setValue('gender', director?.gender);
    setValue('dateBirth', director?.date_of_birth);
    setValue('phone', director?.telefones?.[0].number);
    setValue('email', director?.email);
    setValue('nationality', director?.nationality);
    setValue('passportNo', director?.passports?.[0].number);
    setValue('expiryDate', director?.passports?.[0].expiry_date);
    setValue('passportIssuingCountry', director?.passports?.[0].issuing_country);
    setValue('issueDate', director?.passports?.[0].issuing_date);
    setValue('address1', director?.address?.[0].address);
    setValue('address2', director?.address?.[0].complement);
    setValue('postalCode', director?.address?.[0].postal_code);
    setValue('city', director?.address?.[0].city);
    setValue('state', director?.address?.[0].state);
    setValue('neighborhood', director?.address?.[0].neighborhood);
    setValue('country', director?.address?.[0].country);
  }, [open]);

  const handleFileChangePassport = (e: any) => {
    if (e.target.files.length > 0) {
      setFileNamePassport(e.target.files?.[0].name);
      setValue('passport', e.target.files?.[0]);
    }
  };

  const handleFileChangeDocument1 = (e: any) => {
    if (e.target.files.length > 0) {
      setFileNameDocument1(e.target.files?.[0].name);
      setValue('document1', e.target.files?.[0]);
    }
  };

  // const handleFileChangeDocument2 = (e: any) => {
  //   if (e.target.files.length > 0) {
  //     setFileNameDocument2(e.target.files?.[0].name);
  //     setValue('document2', e.target.files?.[0]);
  //   }
  // };

  const handleFileChangeProveAddress = (e: any) => {
    if (e.target.files.length > 0) {
      setFileNameProveAddress(e.target.files?.[0].name);
      setValue('proveAddress', e.target.files?.[0]);
    }
  };

  const handleSubmitForm: SubmitHandler<FormDataTypeRegisterDirectors> = async (data) => {
    try {
      setLoading(true);

      const headers = {
        Authorization: `Bearer ${token}`,
        'Content-Language': i18n.language.toLowerCase()
      };

      await DirectorsRegisteringService.updateDirectors(api, director.id, { data }, { headers });

      const passportValue = getValues('passport');
      const document1 = getValues('document1');
      // const document2 = getValues('document2');
      const proveAddressValue = getValues('proveAddress');

      if (passportValue.name) {
        try {
          const formData = new FormData();
          formData.append('shareholder_slug', director.slug);
          formData.append('document', passportValue);
          formData.append('document_slug', director.passports?.[0].slug);
          formData.append('action', '3');
          
          // Adicionar informações do passaporte
          formData.append('number', data.passportNo);
          formData.append('issuing_date', (data.issueDate as any)?.toISOString?.()?.split('T')[0] || data.issueDate);
          formData.append('issuing_country', data.passportIssuingCountry);
          formData.append('expiry_date', (data.expiryDate as any)?.toISOString?.()?.split('T')[0] || data.expiryDate);

          await DocumentService.resendDocument(api, formData, {
            headers: {
              ...headers,
              'Content-Type': 'multipart/form-data'
            }
          });
        } catch (e) {
          errorToast(errorMessageAdapter(e));
        }
      }

      if (document1.name) {
        try {
          const formData = {
            shareholder_slug: director.slug,
            document: document1,
            document_slug: director.documents?.[0].slug,
            action: 1
          };

          await DocumentService.resendDocument(api, formData, {
            headers: {
              ...headers,
              'Content-Type': 'multipart/form-data'
            }
          });
        } catch (e) {
          errorToast(errorMessageAdapter(e));
        }
      }

      // if (document2.name) {
      //   try {
      //     const formData = {
      //       shareholder_slug: director.slug,
      //       document: document2,
      //       document_slug: director.documents?.[1].slug,
      //       action: 1
      //     };

      //     await DocumentService.resendDocument(formData, {
      //       headers: {
      //         ...headers,
      //         'Content-Type': 'multipart/form-data'
      //       }
      //     });
      //   } catch (e) {
      //     errorToast(errorMessageAdapter(e));
      //   }
      // }

      if (proveAddressValue?.name) {
        try {
          const formData = {
            shareholder_slug: director.slug,
            document: proveAddressValue,
            document_slug: director.proveAddress?.[0].slug,
            action: 1
          };

          await DocumentService.resendDocument(api, formData, {
            headers: {
              ...headers,
              'Content-Type': 'multipart/form-data'
            }
          });
        } catch (e) {
          errorToast(errorMessageAdapter(e));
        }
      }

      successToast('Director updated successfully');
    } catch (error) {
      console.error({ error });
      errorToast(errorMessageAdapter(error as AxiosError));
    } finally {
      setLoading(false);
      reset();
      onOpenChange(false);
      onChange();
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal className="z-20">
        <Dialog.Overlay className="fixed inset-0 data-[state=open]:animate-overlayShow" />
        <Dialog.Content className="fixed left-[50%] top-[50%] z-20 max-h-[85vh] w-[90vw] max-w-[1220px] translate-x-[-50%] translate-y-[-50%] overflow-x-auto rounded-[6px] bg-white p-[25px] shadow-[hsl(206_22%_7%_/_35%)_0px_10px_38px_-10px,_hsl(206_22%_7%_/_20%)_0px_10px_20px_-15px] focus:outline-none data-[state=open]:animate-contentShow">
          <Dialog.Title className="m-0 text-[17px] font-medium text-mauve12">
            {t('others.othEditDirector')}
          </Dialog.Title>
          <Dialog.Description className="mb-5 mt-[10px] text-[15px] leading-normal text-mauve11">
            {t('others.othMakeChangesProfile')}
          </Dialog.Description>
          <form className="-mt-3.5 bg-white" onSubmit={handleSubmit(handleSubmitForm)}>
            <div className="flex flex-col items-start justify-between gap-4 md:flex-row">
              <div className="mt-6 w-full border-[1px] border-[#E1E9EF] bg-white placeholder:rounded md:w-6/12">
                <span className="block w-full border-b-[1px] p-3 text-lg font-medium">
                  {t('labels.lblInputInformationDirectors')}
                </span>
                <div className="flex flex-col gap-4 p-3">
                  <span className="font-bold">{t('labels.lblDirector')}</span>
                  <input disabled type="hidden" {...register('id')} value={director?.id} />
                  <CustomInput
                    disabled
                    label={t('labels.lblFirstName')}
                    id="name"
                    register={register}
                    error={errors.name?.message}
                  />
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex w-full flex-col gap-1">
                      <label
                        htmlFor="gender"
                        className={`${errors.gender?.message ? 'text-red-500' : 'text-[#0C1FAA]'} text-sm `}
                      >
                        {t('labels.lblGender')}
                      </label>
                      <select
                        disabled
                        id="gender"
                        className={`${
                          errors.gender?.message ? 'border-red-500' : 'border-[#CED4DA]'
                        } h-[40px] w-full rounded-lg border-[1px] bg-white px-4 text-sm outline-none focus:border-blue-700`}
                        {...register('gender')}
                        onChange={(e) => {
                          setValue('gender', e.target.value);
                        }}
                      >
                        <option value="" disabled defaultChecked>
                          {t('labels.lblSelect')}
                        </option>
                        <option value="male">{t('labels.lblGenderMale')}</option>
                        <option value="female">{t('labels.lblGenderFemale')}</option>
                      </select>
                      <span className="h-0 text-xs text-red-500">{errors.gender?.message}</span>
                    </div>
                    <Controller
                      name="dateBirth"
                      control={control}
                      defaultValue=""
                      render={({ field }) => (
                        <Input
                          disabled
                          {...field}
                          label={t('labels.lblDateOfBirth') || ''}
                          error={errors.dateBirth?.message}
                          mask="9999/99/99"
                          onChange={(e) => {
                            setValue('dateBirth', e.target.value);
                          }}
                        />
                      )}
                    />
                  </div>
                  <Controller
                    name="phone"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <CustomInputPhone
                        {...field}
                        disabled
                        label={t('labels.lblPhone')}
                        id="phone"
                        onChange={(v) => setValue('phone', v)}
                        error={errors.phone?.message}
                      />
                    )}
                  />
                  {/* <Controller*/}
                  {/*  name="phone"*/}
                  {/*  control={control}*/}
                  {/*  defaultValue=""*/}
                  {/*  render={({ field }) => (*/}
                  {/*    <CustomInputPhone*/}
                  {/*      {...field}*/}
                  {/*      label={t('labels.lblPhone')}*/}
                  {/*      id="phone"*/}
                  {/*      onChange={(v) => setValue('phone', v)}*/}
                  {/*      error={errors.phone?.message}*/}
                  {/*    />*/}
                  {/*  )}*/}
                  {/* />*/}
                  <CustomInput
                    label={t('labels.lblEmail')}
                    id="email"
                    disabled
                    register={register}
                    error={errors.email?.message}
                  />
                  <div className="flex w-full flex-col gap-1">
                    <label
                      htmlFor="nationality"
                      className={`${errors.nationality?.message ? 'text-red-500' : 'text-[#0C1FAA]'} text-sm `}
                    >
                      {t('labels.lblNationality')}
                    </label>
                    <select
                      disabled
                      id="nationality"
                      className={`${
                        errors.nationality?.message ? 'border-red-500' : 'border-[#CED4DA]'
                      } h-[40px] w-full rounded-lg border-[1px] bg-white px-4 text-sm outline-none focus:border-blue-700`}
                      {...register('nationality')}
                      onChange={(e) => setValue('nationality', e.target.value)}
                    >
                      <option value="" disabled>
                        {t('labels.lblSelect')}
                      </option>
                      {countries[i18n.language as 'pt-BR'].map((country) => (
                        <option key={country.name} value={country.name}>
                          {country.name}
                        </option>
                      ))}
                    </select>
                    <span className="h-0 text-xs text-red-500">{errors.nationality?.message}</span>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <CustomInput
                      disabled
                      label={t('labels.lblPassportNo')}
                      id="passportNo"
                      register={register}
                      error={errors.passportNo?.message}
                    />
                    <Controller
                      name="expiryDate"
                      control={control}
                      defaultValue=""
                      render={({ field }) => (
                        <Input
                          disabled
                          {...field}
                          label={t('labels.lblExpiryDate') || ''}
                          error={errors.expiryDate?.message}
                          value={getValues('expiryDate')}
                          mask="9999/99/99"
                          {...register('expiryDate')}
                          onChange={(e) => setValue('expiryDate', e.target.value)}
                        />
                      )}
                    />
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex w-full flex-col gap-1">
                      <label
                        htmlFor="passportIssuingCountry"
                        className={`${
                          errors.passportIssuingCountry?.message ? 'text-red-500' : 'text-[#0C1FAA]'
                        } text-sm `}
                      >
                        {t('labels.lblPassportIssuingCountry')}
                      </label>
                      <select
                        disabled
                        id="passportIssuingCountry"
                        className={`${
                          errors.passportIssuingCountry?.message ? 'border-red-500' : 'border-[#CED4DA]'
                        } h-[40px] w-full rounded-lg border-[1px] bg-white px-4 text-sm outline-none focus:border-blue-700`}
                        {...register('passportIssuingCountry')}
                        onChange={(e) => setValue('passportIssuingCountry', e.target.value)}
                      >
                        <option value="" disabled>
                          {t('labels.lblSelect')}
                        </option>
                        {countries[i18n.language as 'pt-BR'].map((country) => (
                          <option key={country.name} value={country.name}>
                            {country.name}
                          </option>
                        ))}
                      </select>
                      <span className="h-0 text-xs text-red-500">{errors.passportIssuingCountry?.message}</span>
                    </div>
                    <Controller
                      name="expiryDate"
                      control={control}
                      defaultValue=""
                      render={({ field }) => (
                        <Input
                          disabled
                          {...field}
                          label={t('labels.lblIssueDate') || ''}
                          error={errors.issueDate?.message}
                          mask="9999/99/99"
                          value={getValues('issueDate')}
                          {...register('issueDate')}
                          onChange={(e) => setValue('issueDate', e.target.value)}
                        />
                      )}
                    />
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <CustomInput
                      disabled
                      label={t('labels.lblAddress1')}
                      id="address1"
                      register={register}
                      error={errors.address1?.message}
                    />
                    <CustomInput
                      disabled
                      label={t('labels.lblAddress2')}
                      id="address2"
                      register={register}
                      error={errors.address2?.message}
                    />
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <CustomInput
                      disabled
                      label={t('labels.lblPostalCode')}
                      id="postalCode"
                      register={register}
                      error={errors.postalCode?.message}
                    />
                    <CustomInput
                      disabled
                      label={t('labels.lblCity')}
                      id="city"
                      register={register}
                      error={errors.city?.message}
                    />
                    <CustomInput
                      disabled
                      label={t('labels.lblState')}
                      id="state"
                      register={register}
                      error={errors.state?.message}
                    />
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <CustomInput
                      disabled
                      label={t('labels.lblNeighborhood')}
                      id="neighborhood"
                      register={register}
                      error={errors.neighborhood?.message}
                    />
                    <div className="flex w-full flex-col gap-1">
                      <label
                        htmlFor="country"
                        className={`${errors.country?.message ? 'text-red-500' : 'text-[#0C1FAA]'} text-sm `}
                      >
                        {t('labels.lblCountry')}
                      </label>
                      <select
                        disabled
                        id="country"
                        className={`${
                          errors.country?.message ? 'border-red-500' : 'border-[#CED4DA]'
                        } h-[40px] w-full rounded-lg border-[1px] bg-white px-4 text-sm outline-none focus:border-blue-700`}
                        {...register('country')}
                        onChange={(e) => setValue('country', e.target.value)}
                      >
                        <option value="" disabled>
                          {t('labels.lblSelect')}
                        </option>
                        {countries[i18n.language as 'pt-BR'].map((country) => (
                          <option key={country.name} value={country.name}>
                            {country.name}
                          </option>
                        ))}
                      </select>
                      <span className="h-0 text-xs text-red-500">{errors.country?.message}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6 w-full border-[1px] border-[#E1E9EF] bg-white placeholder:rounded md:w-6/12">
                <span className="block w-full border-b-[1px] p-3 text-lg font-medium">
                  {t('labels.lblUploadDirectorsDocuments')}
                </span>
                <div className="flex flex-col gap-4 p-3">
                  <span className="font-bold">
                    {t('labels.lblPassport')} -<span className="font-normal">{t('others.othDirectors')}</span>
                  </span>
                  <label className="flex h-[200px] w-full cursor-pointer flex-col items-center justify-center rounded border-[1px] border-dashed border-[#0C1FAA] bg-[#E2E5FA]/30 hover:bg-[#E2E5FA]">
                    <img src="/svg/upload.svg" alt="upload" />
                    <span className="text-lg font-bold text-[#0C1FAA]">{t('labels.lblPushYourDocument')}</span>
                    <span className="text text-[#0C1FAA]">{t('labels.lblOrSearchInYourComputer')}</span>
                    <span className="text-xs text-[#0C1FAA]">{t('labels.lblDocumentsSupported')} </span>
                    <input
                      disabled
                      id="passport"
                      type="file"
                      className="hidden"
                      {...register('passport')}
                      onChange={handleFileChangePassport}
                    />
                    {errors.passport?.message && <span className="text-red-500">{errors.passport.message}</span>}
                  </label>
                  {fileNamePassport && <p>{fileNamePassport}</p>}
                </div>

                <div className="flex flex-col gap-4 p-3">
                  <span className="font-bold">
                    Document 1 -<span className="font-normal"> {t('others.othDirectors')}</span>
                  </span>
                  <label className="flex h-[200px] w-full cursor-pointer flex-col items-center justify-center rounded border-[1px] border-dashed border-[#0C1FAA] bg-[#E2E5FA]/30 hover:bg-[#E2E5FA]">
                    <img src="/svg/upload.svg" alt="upload" />
                    <span className="text-lg font-bold text-[#0C1FAA]">{t('labels.lblPushYourDocument')}</span>
                    <span className="text text-[#0C1FAA]">{t('labels.lblOrSearchInYourComputer')}</span>
                    <span className="text-xs text-[#0C1FAA]">{t('labels.lblDocumentsSupported')} </span>
                    <input
                      disabled
                      id="localDocument"
                      type="file"
                      className="hidden"
                      {...register('document1')}
                      onChange={handleFileChangeDocument1}
                    />
                    {errors.document1?.message && <span className="z-10 text-red-500">{errors.document1.message}</span>}
                  </label>
                  {fileNameDocument1 && <p>{fileNameDocument1}</p>}
                </div>

                {/* <div className="flex flex-col gap-4 p-3">
                  <span className="font-bold">
                    Document 2 -<span className="font-normal"> {t('others.othDirectors')}</span>
                  </span>
                  <label className="w-full flex flex-col items-center justify-center bg-[#E2E5FA]/30 hover:bg-[#E2E5FA] cursor-pointer rounded h-[200px] border-[1px] border-dashed border-[#0C1FAA]">
                    <img src="/svg/upload.svg" alt="upload" />
                    <span className="text-lg font-bold text-[#0C1FAA]">{t('labels.lblPushYourDocument')}</span>
                    <span className="text text-[#0C1FAA]">{t('labels.lblOrSearchInYourComputer')}</span>
                    <span className="text-xs text-[#0C1FAA]">{t('labels.lblDocumentsSupported')} </span>
                    <input
                    disabled
                      id="localDocument"
                      type="file"
                      className="hidden"
                      {...register('document2')}
                      onChange={handleFileChangeDocument2}
                    />
                    {errors.document2?.message && <span className="text-red-500 z-10">{errors.document2.message}</span>}
                  </label>
                  {fileNameDocument2 && <p>{fileNameDocument2}</p>}
                </div> */}

                <div className="flex flex-col gap-4 p-3">
                  <span className="font-bold">
                    {t('labels.lblProveAddress')} -<span className="font-normal"> Directors</span>
                  </span>
                  <label className="flex h-[200px] w-full cursor-pointer flex-col items-center justify-center rounded border-[1px] border-dashed border-[#0C1FAA] bg-[#E2E5FA]/30 hover:bg-[#E2E5FA]">
                    <img src="/svg/upload.svg" alt="upload" />
                    <span className="text-lg font-bold text-[#0C1FAA]">{t('labels.lblPushYourDocument')}</span>
                    <span className="text text-[#0C1FAA]">{t('others.othOrSearchInYourComputer')}</span>
                    <span className="text-xs text-[#0C1FAA]">{t('labels.lblDocumentsSupported')} </span>
                    <input
                      disabled
                      id="proveAddress"
                      type="file"
                      className="hidden"
                      {...register('proveAddress')}
                      onChange={handleFileChangeProveAddress}
                    />
                    {errors.proveAddress?.message && (
                      <span className="z-10 text-red-500">{errors.proveAddress.message}</span>
                    )}
                  </label>
                  {fileNameProveAddress && <p>{fileNameProveAddress}</p>}
                </div>
              </div>
            </div>
            {/* <div className="flex justify-end gap-4 mt-10 py-4">
              <MainCustomButton text="Edit" type="submit" loading={loading} />
            </div> */}
          </form>
          <Dialog.Close asChild>
            <button
              className="absolute right-[10px] top-[10px] inline-flex h-[25px] w-[25px] appearance-none items-center justify-center rounded-full text-violet11 hover:bg-violet4 focus:shadow-[0_0_0_2px] focus:shadow-violet7 focus:outline-none"
              aria-label="Close"
            >
              x
            </button>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export { EditDirectorModal };
